import React, { useState, useEffect, useRef } from 'react';
import './Page1.css';
import './Page1-consent.css';
import TextCaptcha from './TextCaptcha';
// The assistant (bottom-right button) lives in its own file: every office, race and question set
import Chatbot from './Chatbot';
const CIVIC_API_KEY = process.env.REACT_APP_CIVIC_API_KEY;
const NEWSLETTER_LAMBDA_URL = 'https://bwv4lypdn3x2nb4nabyhwqpcpi0whzxt.lambda-url.us-east-2.on.aws/';
const API_URL = 'https://0uuj9e2rh5.execute-api.us-east-2.amazonaws.com';
// indy-lookup-districts Lambda Function URL
const DISTRICTS_LAMBDA_URL = 'https://dg27vk5mlxhypfqj2475myd3wa0niuir.lambda-url.us-east-2.on.aws/';
// Terms acceptance (clickwrap): saved by the indy-record-consent Lambda when the user submits the form.
// Paste that Lambda's Function URL here. Update the two versions whenever the documents change.
const CONSENT_LAMBDA_URL = 'https://vskhqnzpheedla74litmr236zq0cpccy.lambda-url.us-east-2.on.aws/';
const TERMS_VERSION = 'July 1, 2026';    // must match "Effective ..." on the Terms of Use page
const PRIVACY_VERSION = 'July 1, 2026';  // must match "Effective ..." on the Privacy Policy page

// ── State Senate + State House lookup (Census district codes for the exact street address) ──
// Never blocks the site: on any problem it returns null and Page 2 shows a friendly message.
async function lookupStateLegislature(street, zip) {
  if (!DISTRICTS_LAMBDA_URL || DISTRICTS_LAMBDA_URL.startsWith('PASTE_')) return null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    const res = await fetch(`${DISTRICTS_LAMBDA_URL}?street=${encodeURIComponent(street)}&zip=${encodeURIComponent(zip)}`, { signal: controller.signal });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// If the street as typed can't be matched (e.g. "1512 frenchmans" with no Dr/Ct), try again with the
// full street Google already worked out for this address (its "normalizedInput", with the street type filled in).
async function retryLegislatureLookup(legislature, typedStreet, zip, normalized) {
  if (legislature && legislature.matched) return legislature;
  const street = [normalized?.line1, normalized?.line2].filter(Boolean).join(' ').trim();
  if (!street || street.toLowerCase() === typedStreet.trim().toLowerCase()) return legislature;
  const second = await lookupStateLegislature(street, (normalized?.zip || zip).slice(0, 5));
  return second && second.matched ? second : legislature;
}

const STATE_NAMES = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',
  CO:'Colorado',CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',
  HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',
  KS:'Kansas',KY:'Kentucky',LA:'Louisiana',ME:'Maine',MD:'Maryland',
  MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',MO:'Missouri',
  MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',
  NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',
  OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',
  SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',
  VA:'Virginia',WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming'
};

function getPartyColor(party) {
  const p = (party || '').toLowerCase();
  if (p.includes('democrat')) return '#3C3B6E';
  if (p.includes('republican')) return '#B22234';
  if (p.includes('libertarian')) return '#F38C00';
  if (p.includes('green')) return '#00A86B';
  if (p.includes('independent')) return '#6B7280';
  return '#808080';
}

const OFFICE_NAMES = {
  'lieutenant-governor': 'Lieutenant Governor', 'attorney-general': 'Attorney General', treasurer: 'State Treasurer',
  'secretary-of-state': 'Secretary of State', 'agriculture-commissioner': 'Agriculture Commissioner',
  'insurance-commissioner': 'Insurance Commissioner', 'labor-commissioner': 'Labor Commissioner',
  'land-commissioner': 'Land Commissioner', 'superintendent-of-schools': 'Superintendent of Schools',
};
const AUDITOR_TITLES = {
  AL: 'State Auditor', AR: 'State Auditor', CA: 'State Controller', CT: 'State Comptroller', DE: 'State Auditor', IA: 'Auditor of State',
  ID: 'State Controller', IL: 'State Comptroller', IN: 'State Comptroller', MA: 'Auditor of the Commonwealth', MD: 'State Comptroller',
  MN: 'State Auditor', MO: 'State Auditor', NE: 'Auditor of Public Accounts', NM: 'State Auditor', NV: 'State Controller',
  NY: 'State Comptroller', OH: 'Auditor of State', OK: 'State Auditor and Inspector', SC: 'Comptroller General', SD: 'State Auditor',
  VT: 'Auditor of Accounts', WY: 'State Auditor',
};

function getRaceLabel(candidate) {
  const state = STATE_NAMES[candidate.state] || candidate.state || '';
  const type = candidate.racetype || '';
  const district = candidate.district || '';
  if (type === 'house') return `${state} — U.S. House District ${district}`;
  if (type === 'senate') return `${state} — U.S. Senate`;
  if (type === 'governor') return `${state} — Governor`;
  if ((type === 'state-senate' || type === 'state-house') && candidate.raceLabel) return `${state} — ${candidate.raceLabel}`;
  // Statewide offices (search results used to show the raw id, e.g. "IL-auditor")
  if (type === 'auditor') return `${state} — ${AUDITOR_TITLES[candidate.state] || 'State Auditor'}`;
  if (OFFICE_NAMES[type]) return `${state} — ${OFFICE_NAMES[type]}`;
  // Boards & councils (Governor's Council, Regents, Board of Education, ...) carry Lisa's race name
  if (candidate.raceLabel) return candidate.raceLabel.includes(state) ? candidate.raceLabel : `${state} — ${candidate.raceLabel}`;
  return candidate.raceId || '';
}

function CandidateSearch({ onCandidateSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (value) => {
    setQuery(value);
    clearTimeout(debounceRef.current);

    if (!value || value.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      setSearching(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${API_URL}/search?q=${encodeURIComponent(value.trim())}`);
        const data = await res.json();
        setResults(data.results || []);
        setShowResults((data.results || []).length > 0);
      } catch {
        setResults([]);
        setShowResults(false);
      }
      setSearching(false);
    }, 350);
  };

  const handleClear = () => {
    clearTimeout(debounceRef.current);
    setQuery('');
    setResults([]);
    setShowResults(false);
    setSearching(false);
  };

  return (
    <div className="candidate-search" ref={searchRef}>
      <div className="candidate-search-box">
        <span className="candidate-search-icon">🔍</span>
        <input
          type="text"
          className="candidate-search-input"
          placeholder="Search any candidate by name..."
          value={query}
          onChange={e => handleSearch(e.target.value)}
          onFocus={() => results.length > 0 && setShowResults(true)}
        />
        {searching && <div className="candidate-search-spinner" />}
        {query && (
          <button className="candidate-search-clear" onClick={handleClear}>✕</button>
        )}
      </div>

      {showResults && results.length > 0 && (
        <div className="candidate-search-results">
          {results.map((candidate, idx) => {
            const color = getPartyColor(candidate.party);
            const initials = candidate.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
            return (
              <button key={idx} className="candidate-search-result"
                onClick={() => { onCandidateSelect(candidate); setShowResults(false); setQuery(''); setResults([]); }}>
                <div className="candidate-search-result-avatar" style={{ background: color }}>
                  {candidate.photo ? (
                    <img src={candidate.photo} alt={candidate.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
                  ) : initials}
                </div>
                <div className="candidate-search-result-info">
                  <div className="candidate-search-result-name">{candidate.name}</div>
                  <div className="candidate-search-result-race">{getRaceLabel(candidate)}</div>
                </div>
                <div className="candidate-search-result-party" style={{ color }}>{candidate.party}</div>
              </button>
            );
          })}
        </div>
      )}

      {showResults && query.length >= 2 && results.length === 0 && !searching && (
        <div className="candidate-search-results">
          <div style={{ padding: '16px', color: '#888', fontSize: '14px', textAlign: 'center' }}>No candidates found for "{query}"</div>
        </div>
      )}
    </div>
  );
}

async function subscribeToBeehiiv(email) {
  if (!email || !email.trim()) return;
  try {
    await fetch(NEWSLETTER_LAMBDA_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim() })
    });
  } catch { }
}

// Saves one record of the Terms/Privacy acceptance for this lookup (date, time, versions, email/address,
// session and a per-lookup id). Runs in the background — never slows down or blocks the lookup.
function newId() {
  try { if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID(); } catch { }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
function getSessionId() {
  try {
    let id = window.sessionStorage.getItem('indySessionId');
    if (!id) { id = newId(); window.sessionStorage.setItem('indySessionId', id); }
    return id;
  } catch { return ''; }
}
// The candidate search bar and the chatbot are shown once the visitor has registered in this browser session:
// valid email + street address + 5-digit ZIP filled in and the Terms/Privacy box checked. No captcha or submit
// needed, so the visitor stays on Page 1. The acceptance is recorded once at that moment.
const REGISTERED_KEY = 'indyRegistered';
function isRegistered() {
  try { return window.sessionStorage.getItem(REGISTERED_KEY) === 'true'; } catch { return false; }
}
function markRegistered() {
  try { window.sessionStorage.setItem(REGISTERED_KEY, 'true'); } catch { }
}

function recordTermsAcceptance({ email, streetAddress, zipCode }) {
  if (!CONSENT_LAMBDA_URL || CONSENT_LAMBDA_URL.startsWith('PASTE_')) return;
  const record = {
    lookupId: newId(),
    sessionId: getSessionId(),
    email: (email || '').trim(),
    streetAddress: (streetAddress || '').trim(),
    zipCode: (zipCode || '').trim(),
    termsVersion: TERMS_VERSION,
    privacyVersion: PRIVACY_VERSION,
    clientTime: new Date().toISOString(),
    page: window.location.href,
  };
  try {
    fetch(CONSENT_LAMBDA_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },   // simple request: no CORS preflight
      body: JSON.stringify(record),
      keepalive: true,                              // still sent if the page changes right away
    }).catch(() => { });
  } catch { }
}

function Page1({ onSubmit, onCandidateSelect, onShowLegal }) {
  const [email, setEmail] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false); // starts unchecked — never pre-checked
  const [captchaOk, setCaptchaOk] = useState(false);          // "type the characters" check
  const [registered, setRegistered] = useState(isRegistered);   // search bar + chatbot only after registering

  // Unlock as soon as email, address and ZIP are filled in and the box is checked
  useEffect(() => {
    if (registered || !agreedToTerms) return;
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
    const zipOk = /^\d{5}$/.test(zipCode);
    if (emailOk && streetAddress.trim() && zipOk) {
      recordTermsAcceptance({ email, streetAddress, zipCode });
      markRegistered();
      setRegistered(true);
    }
  }, [registered, agreedToTerms, email, streetAddress, zipCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Email is required (Lisa): a real address is needed for the newsletter and the acceptance record
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!zipCode || zipCode.length !== 5 || isNaN(zipCode)) {
      setError('Please enter a valid 5-digit zip code.');
      return;
    }

    if (!streetAddress.trim()) {
      setError('Please enter your street address.');
      return;
    }

    // Required: the button is disabled until the box is checked and the characters are typed; second safety check
    if (!agreedToTerms || !captchaOk) return;

    setLoading(true);
    recordTermsAcceptance({ email, streetAddress, zipCode });
    markRegistered();
    setRegistered(true);

    subscribeToBeehiiv(email);

    // Start the State Senate / State House lookup now so it runs alongside the Civic lookup below
    const legislaturePromise = lookupStateLegislature(streetAddress.trim(), zipCode);

    try {
      const fullAddress = `${streetAddress.trim()}, ${zipCode}`;
      const electionId = '9000';
      const url = `https://civicinfo.googleapis.com/civicinfo/v2/voterinfo?address=${encodeURIComponent(fullAddress)}&electionId=${electionId}&key=${CIVIC_API_KEY}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.error) {
        const divUrl = `https://civicinfo.googleapis.com/civicinfo/v2/divisionsByAddress?address=${encodeURIComponent(fullAddress)}&key=${CIVIC_API_KEY}`;
        const divResponse = await fetch(divUrl);
        const divData = await divResponse.json();

        if (divData.error) {
          setError('We could not find district information for this address. Please check and try again.');
          setLoading(false);
          return;
        }

        let state = '';
        let stateName = '';
        const districts = [];

        Object.keys(divData.divisions || {}).forEach(key => {
          if (key.match(/ocd-division\/country:us\/state:[a-z]+$/) && !key.includes('/cd:')) {
            state = key.split('state:')[1].toUpperCase();
            stateName = divData.divisions[key].name || state;
          }
          if (key.includes('/cd:')) {
            const d = key.split('/cd:')[1];
            if (d && !districts.includes(d)) districts.push(d);
            if (!state) {
              const stateMatch = key.match(/state:([a-z]+)/);
              if (stateMatch) state = stateMatch[1].toUpperCase();
            }
          }
        });

        const legislature = await retryLegislatureLookup(await legislaturePromise, streetAddress, zipCode, divData.normalizedInput);
        onSubmit({ zipCode, email, state, stateName, district: districts[0] || '', districts, legislature });
      } else {
        const contests = data.contests || [];
        const normalizedAddress = data.normalizedInput || {};
        const state = (normalizedAddress.state || '').toUpperCase();
        const districts = [];

        contests.forEach(contest => {
          if (contest.office && contest.office.toLowerCase().includes('representative')) {
            if (contest.district && contest.district.name) {
              const match = contest.district.name.match(/(\d+)/);
              if (match && !districts.includes(match[1])) districts.push(match[1]);
            }
            if (districts.length === 0 && contest.office) {
              const match = contest.office.match(/District\s+(\d+)/i);
              if (match && !districts.includes(match[1])) districts.push(match[1]);
            }
          }
        });

        if (districts.length === 0) {
          try {
            const divUrl = `https://civicinfo.googleapis.com/civicinfo/v2/divisionsByAddress?address=${encodeURIComponent(fullAddress)}&key=${CIVIC_API_KEY}`;
            const divResponse = await fetch(divUrl);
            const divData = await divResponse.json();
            Object.keys(divData.divisions || {}).forEach(key => {
              // Try /cd: first (congressional district)
              if (key.includes('/cd:')) {
                const d = key.split('/cd:')[1];
                if (d && !districts.includes(d)) districts.push(d);
              }
            });
            // If still nothing, try to extract district from any key containing state + district number
            if (districts.length === 0) {
              Object.keys(divData.divisions || {}).forEach(key => {
                const match = key.match(/\/cd:(\d+)/);
                if (match && !districts.includes(match[1])) districts.push(match[1]);
              });
            }
          } catch { }
        }

        const legislature = await retryLegislatureLookup(await legislaturePromise, streetAddress, zipCode, normalizedAddress);
        onSubmit({ zipCode, email, state, stateName: state, district: districts[0] || '', districts, contests, normalizedAddress, legislature });
      }
    } catch (err) {
      setError('Something went wrong. Please check your connection and try again.');
      setLoading(false);
    }
  };

  return (
    <div className="page1">
      {registered && (
        <div className="page1-search-bar">
          <div className="page1-search-bar-inner">
            <CandidateSearch onCandidateSelect={onCandidateSelect} />
          </div>
        </div>
      )}

      <div className="page1-hero">
        <div className="page1-overlay" />
        <div className="page1-content fade-in">
          <a href="https://www.independenttimes.news" target="_blank" rel="noopener noreferrer" className="page1-logo-link">
            <img src="/indtimes-logo.png" alt="Independent Times News" className="page1-logo" />
          </a>
          <p className="page1-eyebrow">YOUR VOTE MATTERS</p>
          <h1 className="page1-title">
            Side-by-Side:<br />
            Compare Major Party<br />
            Candidates for the<br />
            November 2026 Election
          </h1>
          <p className="page1-subtitle">
            Enter your address to see the candidates on your ballot
          </p>

          <form className="page1-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email — Stay Informed with Independent Times News Dispatches (unsubscribe anytime)</label>
              <input type="email" className="form-input" placeholder="example@domain.com" value={email} onChange={e => setEmail(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">Street Address</label>
              <input type="text" className="form-input" placeholder="123 Main St" value={streetAddress} onChange={e => setStreetAddress(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">Zip Code</label>
              <input type="text" className="form-input" placeholder="Five Digits" value={zipCode} onChange={e => setZipCode(e.target.value.slice(0, 5))} maxLength={5} required />
            </div>
            {error && <div className="error-message">⚠️ {error}</div>}
            <TextCaptcha onChange={setCaptchaOk} />
            <label className="page1-consent">
              <input
                type="checkbox"
                className="page1-consent-box"
                checked={agreedToTerms}
                onChange={e => setAgreedToTerms(e.target.checked)}
              />
              <span>
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="page1-consent-link">Terms of Use</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="page1-consent-link">Privacy Policy</a>.
              </span>
            </label>
            <button type="submit" className="btn-primary" disabled={loading || !agreedToTerms || !captchaOk}>
              {loading ? <span className="loading-spinner" /> : 'See My Candidates'}
            </button>
          </form>

          {/* Footer text — bold and brighter so it is easy to read on the photo */}
          <div className="page1-footer">
            Powered by IndTimes.news · Non-partisan · Independent Voter Resource
            <div className="page1-disclaimer">
              <div>
                <strong>Disclaimer:</strong>{' '}
                We work hard to keep candidate information accurate, but always verify with your official state election office.
              </div>
              <div style={{ marginTop: '4px' }}>
                See an error? Email{' '}
                <a href="mailto:corrections@indtimes.news" className="page1-disclaimer-link">corrections@indtimes.news</a>,
                so we can update it promptly.
              </div>
            </div>
          </div>
          <div className="page1-legal-links">
            <button type="button" className="page1-legal-link" onClick={() => onShowLegal && onShowLegal('privacy')}>
              Privacy Policy
            </button>
            <span className="page1-legal-sep">·</span>
            <button type="button" className="page1-legal-link" onClick={() => onShowLegal && onShowLegal('terms')}>
              Terms of Use
            </button>
          </div>
        </div>
      </div>

      {registered && <Chatbot />}
     
    </div>
  );
}

export default Page1;